import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Progress, List, Tag, Button, notification, Timeline, Spin, Alert, Form, Input, Select, Modal } from 'antd';
import { 
  RobotOutlined as BrainIcon, 
  RiseOutlined as TrendingUpIcon, 
  ReloadOutlined as RefreshCwIcon,
  CheckCircleOutlined as CheckCircleIcon,
  ExclamationCircleOutlined as AlertTriangleIcon,
  MessageOutlined as MessageSquareIcon,
  ClockCircleOutlined as ClockIcon,
  UserOutlined as UserIcon,
  LikeOutlined as ThumbsUpIcon,
  DislikeOutlined as ThumbsDownIcon
} from '@ant-design/icons';
import { aiService } from '../../services/ai';

const { TextArea } = Input;
const { Option } = Select;

interface FeedbackItem {
  id: string;
  feedback_type: string;
  user_id: string;
  original_prediction: any;
  correct_response: any;
  feedback_text: string;
  created_at: string;
  status: string;
  confidence_level: string;
}

const RealTimeLearningDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [trainingStatus, setTrainingStatus] = useState<any>(null);
  const [feedbackModal, setFeedbackModal] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [form] = Form.useForm();

  // Mock feedback data - gerçek API'den gelecek
  const [feedbackHistory] = useState<FeedbackItem[]>([
    {
      id: '1',
      feedback_type: 'threat_correction',
      user_id: 'admin@getmyhive.com',
      original_prediction: { threat_level: 'low', confidence: 0.3 },
      correct_response: { threat_level: 'high', confidence: 0.9 },
      feedback_text: 'Bu gerçekte yüksek seviye güvenlik tehdidi - AI yanılmış',
      created_at: new Date(Date.now() - 1000 * 60 * 15).toISOString(), // 15 dakika önce
      status: 'processed',
      confidence_level: 'very_confident'
    },
    {
      id: '2',
      feedback_type: 'false_positive',
      user_id: 'security@getmyhive.com',
      original_prediction: { threat_level: 'high', confidence: 0.8 },
      correct_response: { threat_level: 'normal', confidence: 0.95 },
      feedback_text: 'Yanlış alarm - normal kullanıcı aktivitesi',
      created_at: new Date(Date.now() - 1000 * 60 * 45).toISOString(), // 45 dakika önce
      status: 'integrated',
      confidence_level: 'confident'
    },
    {
      id: '3',
      feedback_type: 'performance_feedback',
      user_id: 'ops@getmyhive.com',
      original_prediction: { response_time: 'slow' },
      correct_response: { optimization: 'needed' },
      feedback_text: 'Analiz süresi çok uzun, hızlandırılmalı',
      created_at: new Date(Date.now() - 1000 * 60 * 120).toISOString(), // 2 saat önce
      status: 'pending',
      confidence_level: 'confident'
    }
  ]);

  const fetchTrainingData = async () => {
    try {
      setRefreshing(true);
      const status = await aiService.getTrainingStatus();
      setTrainingStatus(status);
      
      notification.success({
        message: '🧠 Real-time Learning Verileri Güncellendi',
        description: 'Öğrenme metrikleri başarıyla yüklendi.',
        placement: 'topRight'
      });
    } catch (error) {
      console.error('Training data fetch error:', error);
      notification.error({
        message: '❌ Veri Yüklenemedi',
        description: 'Real-time learning verileri alınamadı.',
        placement: 'topRight'
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const submitFeedback = async (values: any) => {
    try {
      setSubmittingFeedback(true);
      
      const feedbackData = {
        feedback_type: values.feedback_type,
        user_id: 'current_user@getmyhive.com', // Gerçek kullanıcı ID'si gelecek
        original_prediction: JSON.parse(values.original_prediction || '{}'),
        correct_response: JSON.parse(values.correct_response || '{}'),
        feedback_text: values.feedback_text
      };

      await aiService.submitFeedback(feedbackData);
      
      notification.success({
        message: '✅ Feedback Gönderildi',
        description: 'AI sistemi geri bildiriminizi aldı ve öğrenmeye başladı.',
        placement: 'topRight'
      });
      
      setFeedbackModal(false);
      form.resetFields();
      fetchTrainingData(); // Verileri yenile
      
    } catch (error) {
      console.error('Feedback submission error:', error);
      notification.error({
        message: '❌ Feedback Gönderilemedi', 
        description: 'Geri bildirim gönderilirken hata oluştu.',
        placement: 'topRight'
      });
    } finally {
      setSubmittingFeedback(false);
    }
  };

  useEffect(() => {
    fetchTrainingData();
    
    // Auto refresh every 30 seconds
    const interval = setInterval(fetchTrainingData, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'processed': return 'green';
      case 'integrated': return 'blue';
      case 'pending': return 'orange';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'processed': return <CheckCircleIcon  />;
      case 'integrated': return <ThumbsUpIcon  />;
      case 'pending': return <ClockIcon  />;
      default: return <AlertTriangleIcon  />;
    }
  };

  const getFeedbackTypeIcon = (type: string) => {
    switch (type) {
      case 'threat_correction': return '🚨';
      case 'false_positive': return '⚠️';
      case 'performance_feedback': return '⚡';
      default: return '💬';
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <p style={{ marginTop: '16px', fontSize: '16px' }}>🔄 Real-time Learning Verileri Yükleniyor...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 600 }}>
            🔄 Real-time Learning Dashboard
          </h2>
          <p style={{ margin: '4px 0 0 0', color: '#666' }}>
            AI'nın gerçek zamanlı öğrenme süreci ve kullanıcı geri bildirimleri
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button 
            icon={<MessageSquareIcon  />}
            onClick={() => setFeedbackModal(true)}
            type="primary"
          >
            Feedback Gönder
          </Button>
          <Button 
            icon={<RefreshCwIcon  />}
            onClick={fetchTrainingData}
            loading={refreshing}
          >
            Yenile
          </Button>
        </div>
      </div>

      {/* Learning Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Toplam Feedback"
              value={trainingStatus?.training_metrics?.feedback_samples_collected || feedbackHistory.length}
              prefix={<MessageSquareIcon  />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="İşlenen Feedback"
              value={feedbackHistory.filter(f => f.status === 'processed' || f.status === 'integrated').length}
              prefix={<CheckCircleIcon  />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Bekleyen Feedback"
              value={feedbackHistory.filter(f => f.status === 'pending').length}
              prefix={<ClockIcon  />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Öğrenme Oranı"
              value={feedbackHistory.length > 0 ? Math.round((feedbackHistory.filter(f => f.status !== 'pending').length / feedbackHistory.length) * 100) : 0}
              suffix="%"
              prefix={<BrainIcon  />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* Feedback Timeline */}
        <Col xs={24} lg={12}>
          <Card 
            title="📝 Son Feedback'ler"
            extra={<Tag color="blue">Canlı</Tag>}
            style={{ height: '500px', overflowY: 'auto' }}
          >
            <List
              dataSource={feedbackHistory}
              renderItem={(feedback) => (
                <List.Item style={{ borderBottom: '1px solid #f0f0f0', paddingBottom: '16px', marginBottom: '16px' }}>
                  <List.Item.Meta
                    avatar={
                      <div style={{ fontSize: '24px' }}>
                        {getFeedbackTypeIcon(feedback.feedback_type)}
                      </div>
                    }
                    title={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 600 }}>
                          {feedback.feedback_type === 'threat_correction' && 'Tehdit Düzeltmesi'}
                          {feedback.feedback_type === 'false_positive' && 'Yanlış Alarm'}
                          {feedback.feedback_type === 'performance_feedback' && 'Performans Geri Bildirimi'}
                        </span>
                        <Tag color={getStatusColor(feedback.status)} icon={getStatusIcon(feedback.status)}>
                          {feedback.status === 'processed' && 'İşlendi'}
                          {feedback.status === 'integrated' && 'Entegre Edildi'}
                          {feedback.status === 'pending' && 'Bekliyor'}
                        </Tag>
                      </div>
                    }
                    description={
                      <div style={{ marginTop: '8px' }}>
                        <div style={{ marginBottom: '8px', fontSize: '14px' }}>
                          {feedback.feedback_text}
                        </div>
                        <div style={{ fontSize: '12px', color: '#999', display: 'flex', justifyContent: 'space-between' }}>
                          <span>
                            <UserIcon size={12} style={{ marginRight: '4px' }} />
                            {feedback.user_id}
                          </span>
                          <span>
                            {new Date(feedback.created_at).toLocaleString('tr-TR')}
                          </span>
                        </div>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>

        {/* Learning Progress */}
        <Col xs={24} lg={12}>
          <Card 
            title="📈 Öğrenme Süreci"
            extra={<Tag color="green">Aktif</Tag>}
            style={{ height: '500px' }}
          >
            <Timeline
              items={[
                {
                  color: 'green',
                  children: (
                    <div>
                      <strong>Yeni Feedback İşlendi</strong>
                      <br />
                      <small style={{ color: '#999' }}>
                        Threat correction feedback entegre edildi | 15 dakika önce
                      </small>
                    </div>
                  ),
                },
                {
                  color: 'blue',
                  children: (
                    <div>
                      <strong>Model Güncellendi</strong>
                      <br />
                      <small style={{ color: '#999' }}>
                        False positive feedback'ten öğrenildi | 45 dakika önce
                      </small>
                    </div>
                  ),
                },
                {
                  color: 'orange',
                  children: (
                    <div>
                      <strong>Performans İyileştirmesi</strong>
                      <br />
                      <small style={{ color: '#999' }}>
                        Analiz hızı optimize edildi | 2 saat önce
                      </small>
                    </div>
                  ),
                },
                {
                  color: 'purple',
                  children: (
                    <div>
                      <strong>Otomatik Öğrenme</strong>
                      <br />
                      <small style={{ color: '#999' }}>
                        Kullanıcı davranış patterns'ı güncellendi | 3 saat önce
                      </small>
                    </div>
                  ),
                },
              ]}
            />
            
            <div style={{ marginTop: '24px', padding: '16px', backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '6px' }}>
              <div style={{ fontWeight: 600, color: '#389e0d', marginBottom: '8px' }}>
                🎯 Öğrenme Durumu
              </div>
              <Progress 
                percent={75} 
                status="active" 
                strokeColor="#52c41a"
                format={(percent) => `${percent}% Öğrenildi`}
              />
              <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                AI sistemi sürekli öğrenmeye devam ediyor...
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      {/* Feedback Modal */}
      <Modal
        title="💬 AI'ya Feedback Gönder"
        open={feedbackModal}
        onCancel={() => setFeedbackModal(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={submitFeedback}
        >
          <Form.Item
            name="feedback_type"
            label="Feedback Türü"
            rules={[{ required: true, message: 'Feedback türünü seçin' }]}
          >
            <Select placeholder="Feedback türünü seçin">
              <Option value="threat_correction">🚨 Tehdit Düzeltmesi</Option>
              <Option value="false_positive">⚠️ Yanlış Alarm</Option>
              <Option value="performance_feedback">⚡ Performans Geri Bildirimi</Option>
              <Option value="feature_request">💡 Özellik İsteği</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="original_prediction"
            label="Orijinal AI Tahmini (JSON)"
            rules={[{ required: true, message: 'Orijinal tahmin verisi girin' }]}
          >
            <TextArea 
              rows={3} 
              placeholder='{"threat_level": "low", "confidence": 0.3}'
            />
          </Form.Item>

          <Form.Item
            name="correct_response"
            label="Doğru Cevap (JSON)"
            rules={[{ required: true, message: 'Doğru cevap verisi girin' }]}
          >
            <TextArea 
              rows={3} 
              placeholder='{"threat_level": "high", "confidence": 0.9}'
            />
          </Form.Item>

          <Form.Item
            name="feedback_text"
            label="Açıklama"
            rules={[{ required: true, message: 'Açıklama girin' }]}
          >
            <TextArea 
              rows={4} 
              placeholder="AI'nın hangi konuda yanıldığını ve doğru cevabın ne olduğunu açıklayın..."
            />
          </Form.Item>

          <Form.Item style={{ marginTop: '24px', textAlign: 'right' }}>
            <Button 
              style={{ marginRight: '8px' }}
              onClick={() => setFeedbackModal(false)}
            >
              İptal
            </Button>
            <Button 
              type="primary" 
              htmlType="submit"
              loading={submittingFeedback}
              icon={<MessageSquareIcon  />}
            >
              Feedback Gönder
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RealTimeLearningDashboard;
